import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Lottie from 'lottie-react';
import { CaretLeft, CircleNotch, UserFocus } from '@phosphor-icons/react';
import { Button, Input, Modal, notify } from 'app/components';
import useLoginModal from './useLoginModal';
import CreateAccount from './createAccount';
import { saveProfile, sendEmail, signup, testSignup } from './api';
import './style'
import { analyticEventLogger } from '../../utils/commonUtils';
import { LOGIN_EVENT_CLICK } from '../../constants/eventLogger';

const LoginModal = () => {
  const scannerApp = true;
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [isMailClicked, setIsMailClicked] = useState(false);
  const [showGoogleSignIn, setShowGoogleSignIn] = useState(true);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [emailEmptyMsg, setEmailEmptyMsg] = useState(null);
  const [resendLinkCount, setIsResendLinkCount] = useState(0);
  const [seconds, setSeconds] = useState(30);
  const interval = useRef(null);
  const loginModal = useLoginModal();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const { userData } = useSelector((state) => state.app);

  const modalClose = () => {
    loginModal.onClose();
  };

  useEffect(() => {
    if (window.google) {
      window.google.accounts.id.initialize({
        client_id:
          '16541858401-7f1kud2eu62f3vjasftihb99en2n98dd.apps.googleusercontent.com',
        callback: (response) => {
          analyticEventLogger(LOGIN_EVENT_CLICK, { logginSuccess: response?.credential });
          signup(response, () => {
            handleLoginSuccess();
            modalClose();
          });
        },
      });

      window.google.accounts.id.renderButton(
        document.getElementById('signUpDiv'),
        {
          type: 'standard',
          size: 'extra_large',
          width: 328,
          height: 52,
          theme: 'filled', // or "filled"
          shape: 'rectangular', // or "pill"
        },
      );
    }
  }, [loginModal?.isOpen, showGoogleSignIn, isMailClicked]);

  useEffect(() => {
    if (userData?.email && params?.get('token')) {
      if (userData?.name) {
        handleLoginSuccess();
        navigate('/');
      } else if (!email) setEmail(userData.email);
    }
  }, [userData]);

  const handleLoginSuccess = () => {
    if (loginModal?.callback?.current) {
      loginModal.callback.current();
      loginModal.callback.current = null;
    }
    window.fbq("track", "CompleteRegistration");
    notify('success', 'Login Succesful');
  };

  const validateEmail = (email) => {
    return email?.match(
      /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    );
  };

  const onClickContinueWithMail = () => {
    setIsMailClicked(true);
    setShowGoogleSignIn(email?.includes('gmail.com'));
  };

  const onClickContinueWithEmail = () => {
    if (!isLoading && !resendLinkCount) {
      if (email?.trim()?.length && validateEmail(email?.trim())) {
        setIsLoading(true);
        sendEmail(email).then((data) => {
          setIsLoading(false);
          setIsEmailSent(true);
          setIsResendLinkCount((prev) => prev + 1);
          setSeconds(30);
        });

      } else {
        setEmailEmptyMsg('Please enter a valid email to proceed');
      }
    }
  };

  useMemo(() => {
    if (resendLinkCount) {
      interval.current = setInterval(() => {
        if (seconds) setSeconds((prev) => prev - 1);
      }, 1000);
    } else clearInterval(interval.current);
  }, [resendLinkCount]);

  const resendLabel = useMemo(() => {
    if (seconds === 0) {
      setIsResendLinkCount(0);
      return 'Resend Link';
    }
    if (resendLinkCount) return `Resend Link in ${seconds} seconds...`;

    return 'Resend Link';
  }, [resendLinkCount, seconds]);

  const onNameChange = (e) => {
    setUsername(e?.target?.value);
    if (emailEmptyMsg) setEmailEmptyMsg(null);
  };

  const onCreateAccount = async () => {
    if (!isLoading) {
      if (username?.trim()?.length) {
        const payload = {
          name: username?.trim(),
          mobile: '',
          whatsappNumber: '',
          profile: '',
        };

        // let form = new FormData();
        // form.append('imageOption', 0);
        // form.append('username', username.trim());
        // form.append('image', null);
        // form.append('isNewUser', true);

        setIsLoading(true);
        saveProfile(payload).then(() => {
          setIsLoading(false);
          modalClose();
        });
      } else {
        // reuse same state for username validation as well
        setEmailEmptyMsg('Please enter a name to proceed');
      }
    }
  };

  const onEmailChange = (e) => {
    if (emailEmptyMsg) {
      setEmailEmptyMsg(null);
    }
    setEmail(e.target.value);
    const isValidEmail = validateEmail(e.target.value);
    if (isValidEmail) {
      setShowGoogleSignIn(e.target.value.includes('gmail.com'));
    }
  };

  // mock
  const onContinueWithTestLogin = () => {
    testSignup(
      {
        email: 'selvatmtautomationuser@gmail.com',
      },
      () => {
        handleLoginSuccess();
        modalClose();
      },
    );
  };

  return (
    <>
     {loginModal?.QR ? (
        <Modal
          size="regular"
          className="login-modal"
          title={<img
            src="assets/images/logo-primary.svg"
            alt="logo"
            width={64}
            height={40}
          />}
          isOpen={loginModal?.isOpen}
          onClose={modalClose}
          hasClose={true}
        >
          <div className="p-2 h-full flex flex-col items-center justify-start gap-[16px] sm:gap-[24px]">
            {params?.get('token') ? (
              <CreateAccount
                email={email}
                name={username}
                onNameChange={onNameChange}
                nameValidationMsg={emailEmptyMsg}
                onCreateAccount={onCreateAccount}
                isLoading={isLoading}
              />
            ) : isEmailSent ? (
              <div className="flex flex-col flex-1">
                <Lottie
                  className="mail-lottie"
                  // animationData={Mail}
                  loop={true}
                />
                <div className="text-2xl font-semibold pt-6">
                  Check your inbox for a link
                </div>
                <div className="text-sm sm:text-lg pt-2 sm:pt-3 pb-9">
                  Click the link we sent to{' '}
                  <span className="text-primary">{email}</span> to sign
                  in.
                </div>
                <Button
                  type="tertiary"
                  className="w-full"
                  disabled={seconds !== 0}
                  loading={isLoading}
                  onClick={onClickContinueWithEmail}
                >
                  {resendLabel}
                </Button>
              </div>
            ) : (
              <>
                <div className="heading-24 pb-4">
                  Login or Sign up to Takemytickets
                </div>
                {isMailClicked ? (
                  <>
                    <Input
                      label="Email"
                      key="user-email"
                      type="email"
                      width="100%"
                      {...(emailEmptyMsg && ({ validationStatus: 'error', helpText: emailEmptyMsg }))}
                      value={email}
                      onChange={onEmailChange}
                    />
                    <Button
                      onClick={onClickContinueWithEmail}
                      className="w-full"
                      loading={isLoading}
                    >
                      Continue
                    </Button>
                    {showGoogleSignIn && (
                      <div
                        id="signUpDiv"
                        className="text-[#101010] font-semibold w-full flex items-center justify-center"
                      ></div>)}
                    <Button
                      type="tertiary-text"
                      className="signin-options-button"
                      iconLeft={<CaretLeft />}
                      onClick={() => {
                        setIsMailClicked(false);
                        setShowGoogleSignIn(true);
                      }}
                    >
                      All Sign in options
                    </Button>
                  </>
                ) : (
                  <>
                    {showGoogleSignIn && (
                      <div
                        id="signUpDiv"
                        className="text-[#101010] font-semibold w-full flex items-center justify-center"
                      ></div>)}
                    <div className="relative w-full h-[22px] text-base text-secondary-grey">
                      <div className="absolute top-[12.5px] left-[-0.5px] box-border md:w-full w-full h-px border-t-[1px] border-solid border-date-stroke" />
                      <div className="absolute left-[42%] md:left-[calc(50%_-_11px)] bg-white flex flex-col py-0 px-1 items-start justify-start">
                        <div className="relative">or</div>
                      </div>
                    </div>
                    {/* Mock login button for testing */}
                    {!!process.env.appConfig.IS_TEST_ENV && (
                      <Button
                        type="tertiary"
                        className="test-login-btn w-full"
                        onClick={onContinueWithTestLogin}
                      >
                        Continue with Test Login
                      </Button>)}
                    <Button
                      type="tertiary"
                      className="w-full"
                      onClick={onClickContinueWithMail}
                    >
                      Continue with email
                    </Button>
                  </>
                )}
              </>
            )}
          </div>
        </Modal>
      ) : (
        <Modal
          className="w-full p-6"
          isOpen={loginModal?.isOpen}
          onClose={modalClose}
          hasClose={true}
          size="w-[1440px] h-[650px] p-4"
        >
          <div className="flex flex-col sm:flex-row items-center justify-center h-full gap-6 p-6 pb-8">
            <div className="flex flex-col gap-2 items-center justify-center text-center bg-pink-50 rounded-lg p-4 w-full sm:w-[583px] h-[600px] mb-6">
              <div className="flex items-center gap-3">
                <div className="bg-white p-3 rounded-lg flex items-center justify-center">
                  <UserFocus size={40} className="text-gray-800" color="#FF2459" />
                </div>
                <h2 className="text-lg font-semibold text-gray-800 text-[32px]">  Quick Login</h2>
              </div>
              <p className="text-[16px] text-gray-600 mt-2">Scan the QR code for instant mobile access</p>

              {/* QR Code Container */}
              <div className="mt-6 p-4 border-4 border-pink-500 rounded-lg bg-white">
                <img src="assets/images/QRCode.png" alt="QR Code" className="w-[300px] h-[300px] object-cover" />
              </div>
            </div>

            <div className="flex flex-col items-center justify-center w-full sm:w-1/2 min-w-[400px] p-8 gap-6 border border-gray-300 rounded-lg">
              {params?.get('token') ? (
                <CreateAccount
                  email={email}
                  name={username}
                  onNameChange={onNameChange}
                  nameValidationMsg={emailEmptyMsg}
                  onCreateAccount={onCreateAccount}
                  isLoading={isLoading}
                />
              ) : isEmailSent ? (
                <div className="flex flex-col flex-1">
                  <Lottie
                    className="mail-lottie"
                    // animationData={Mail}
                    loop={true}
                  />
                  <div className="text-2xl font-semibold pt-6">
                    Check your inbox for a link
                  </div>
                  <div className="text-sm sm:text-lg pt-2 sm:pt-3 pb-9">
                    Click the link we sent to{' '}
                    <span className="text-primary">{email}</span> to sign
                    in.
                  </div>
                  <Button
                    type="tertiary"
                    className="w-full"
                    disabled={seconds !== 0}
                    loading={isLoading}
                    onClick={onClickContinueWithEmail}
                  >
                    {resendLabel}
                  </Button>
                </div>
              ) : (
                <>
                  <div className="heading-24 pb-4">
                    Login or Sign up to Takemytickets
                  </div>
                  {isMailClicked ? (
                    <>
                      <Input
                        label="Email"
                        key="user-email"
                        type="email"
                        width="100%"
                        {...(emailEmptyMsg && ({ validationStatus: 'error', helpText: emailEmptyMsg }))}
                        value={email}
                        onChange={onEmailChange}
                      />
                      <Button
                        onClick={onClickContinueWithEmail}
                        className="w-full"
                        loading={isLoading}
                      >
                        Continue
                      </Button>
                      {showGoogleSignIn && (
                        <div
                          id="signUpDiv"
                          className="text-[#101010] font-semibold w-full flex items-center justify-center"
                        ></div>)}
                      <Button
                        type="tertiary-text"
                        className="signin-options-button"
                        iconLeft={<CaretLeft />}
                        onClick={() => {
                          setIsMailClicked(false);
                          setShowGoogleSignIn(true);
                        }}
                      >
                        All Sign in options
                      </Button>
                    </>
                  ) : (
                    <>
                      {showGoogleSignIn && (
                        <div
                          id="signUpDiv"
                          className="text-[#101010] font-semibold w-full flex items-center justify-center"
                        ></div>)}
                      <div className="relative w-full h-[22px] text-base text-secondary-grey">
                        <div className="absolute top-[12.5px] left-[-0.5px] box-border md:w-full w-full h-px border-t-[1px] border-solid border-date-stroke" />
                        <div className="absolute left-[42%] md:left-[calc(50%_-_11px)] bg-white flex flex-col py-0 px-1 items-start justify-start">
                          <div className="relative">or</div>
                        </div>
                      </div>
                      {/* Mock login button for testing */}
                      {!!process.env.appConfig.IS_TEST_ENV && (
                        <Button
                          type="tertiary"
                          className="test-login-btn w-full"
                          onClick={onContinueWithTestLogin}
                        >
                          Continue with Test Login
                        </Button>)}
                      <Button
                        type="tertiary"
                        className="w-full"
                        onClick={onClickContinueWithMail}
                      >
                        Continue with email
                      </Button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </Modal>)}
    </>
  );
};

export default LoginModal;
